import React from 'react';
import { 
  Box, 
  Flex, 
  Text, 
  Icon, 
  Heading, 
  VStack,
  HStack,
  Button,
  Circle,
  useColorModeValue,
  Image
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Mail, 
  Phone,
  Globe,
  Linkedin,
  ExternalLink
} from 'lucide-react';
import NeuralBackground from '../../components/NeuralBackground';
import SlideContainer from '../../components/SlideContainer';
import { MethodistSlide } from '../../components/base/MethodistSlide';
import JIMILogo from '../../components/JIMILogo';

const ThankYouSlide: React.FC = () => {
  // Colors
  const methodistBlue = "#0066CC";
  const methodistGreen = "#4A8B71";
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Contact information
  const contactInfo = [
    {
      label: "Email",
      value: "jimi.wellness@methodist.org",
      icon: Mail,
      action: "mailto:jimi.wellness@methodist.org",
      color: methodistBlue
    },
    {
      label: "Phone",
      value: "(713) 555-1234",
      icon: Phone,
      action: "tel:7135551234",
      color: methodistGreen
    },
    {
      label: "Website",
      value: "jimi.methodist.org",
      icon: Globe,
      action: "https://jimi.methodist.org",
      color: "#805AD5" // Purple
    },
    {
      label: "LinkedIn",
      value: "linkedin.com/company/jimi-wellness",
      icon: Linkedin,
      action: "https://linkedin.com/company/jimi-wellness",
      color: "#0077B5" // LinkedIn blue
    }
  ];
  
  return (
    <MethodistSlide>
      <SlideContainer>
        <Box position="relative" height="100%" overflow="hidden">
          <Box position="absolute" top={0} right={0} bottom={0} left={0} zIndex={-1} opacity={0.1}>
            <NeuralBackground />
          </Box>
          
          <Flex 
            direction="column" 
            align="center" 
            justify="center" 
            textAlign="center" 
            height="100%" 
            p={6}
          > 
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
            >
              <JIMILogo height="100px" mb={6} />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
            >
              <Heading 
                size="2xl" 
                mb={4} 
                bgGradient={`linear(to-r, ${methodistBlue}, ${methodistGreen})`}
                bgClip="text"
              >
                Thank You
              </Heading>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.7 }}
            >
              <Text fontSize="xl" color="gray.600" maxW="700px" mb={8}>
                For your time and consideration of the JIMI Wellness Platform. 
                We look forward to working together to enhance clinician wellbeing.
              </Text>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              style={{ width: "100%", maxWidth: "700px" }}
            >
              <Box 
                bg="white" 
                borderRadius="lg" 
                boxShadow="md" 
                p={6} 
                borderWidth="1px" 
                borderColor={borderColor}
                mb={8}
              >
                <Heading size="md" mb={4} color={methodistBlue}>
                  Contact Information
                </Heading>
                
                <Flex 
                  wrap="wrap" 
                  justify="center" 
                  gap={4}
                >
                  {contactInfo.map((contact, index) => (
                    <Box 
                      key={index} 
                      p={4} 
                      borderRadius="md" 
                      borderWidth="1px" 
                      borderColor="gray.200"
                      width={{ base: "100%", md: "45%" }}
                      textAlign="left"
                    >
                      <HStack spacing={3} mb={2}>
                        <Circle size="36px" bg={`${contact.color}15`} color={contact.color} flexShrink={0}>
                          <Icon as={contact.icon} boxSize={4} />
                        </Circle>
                        <Text fontWeight="medium" color="gray.700">
                          {contact.label}
                        </Text>
                      </HStack>
                      
                      <Button 
                        as="a" 
                        href={contact.action} 
                        target="_blank" 
                        variant="ghost" 
                        colorScheme="blue" 
                        size="sm"
                        justifyContent="flex-start"
                        width="100%"
                        rightIcon={<Icon as={ExternalLink} boxSize={3} />}
                      >
                        {contact.value}
                      </Button>
                    </Box>
                  ))}
                </Flex>
              </Box>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
            >
              <HStack spacing={4}>
                <Button 
                  leftIcon={<Icon as={Heart} />} 
                  colorScheme="blue" 
                  size="lg"
                  onClick={() => window.open('https://jimi.methodist.org/schedule-demo', '_blank')}
                >
                  Schedule a Demo
                </Button>
                <Button 
                  leftIcon={<Icon as={Mail} />} 
                  colorScheme="green" 
                  size="lg"
                  variant="outline"
                  onClick={() => window.open('mailto:jimi.wellness@methodist.org?subject=JIMI%20Wellness%20Platform%20Inquiry', '_blank')}
                >
                  Contact Us
                </Button>
              </HStack>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 1 }}
            >
              <Text fontSize="sm" color="gray.500" mt={12}>
                © {new Date().getFullYear()} Houston Methodist. All rights reserved.
              </Text>
            </motion.div>
          </Flex>
        </Box>
      </SlideContainer>
    </MethodistSlide>
  );
};

export default ThankYouSlide;
